package pl.mothdigital.neuronowi.pages.specialists

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobweb.silk.components.graphics.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.*
import com.varabyte.kobweb.silk.style.breakpoint.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.*
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.common.model.SpecialistData
import pl.mothdigital.neuronowi.common.resources.*
import pl.mothdigital.neuronowi.components.*
import pl.mothdigital.neuronowi.components.layouts.*
import pl.mothdigital.neuronowi.components.sections.cta.*
import pl.mothdigital.neuronowi.components.text.*
import pl.mothdigital.neuronowi.common.utils.fixPolishOrphans
import pl.mothdigital.neuronowi.theme.*

private val priceItems = listOf(
    PriceItem(
        StringResources.ZAKLINA_WYKRET_PRICE_1_NAME,
        StringResources.ZAKLINA_WYKRET_PRICE_1_TIME,
        StringResources.ZAKLINA_WYKRET_PRICE_1_AMOUNT
    ),
    PriceItem(
        StringResources.ZAKLINA_WYKRET_PRICE_2_NAME,
        StringResources.ZAKLINA_WYKRET_PRICE_2_TIME,
        StringResources.ZAKLINA_WYKRET_PRICE_2_AMOUNT
    ),
    PriceItem(
        StringResources.ZAKLINA_WYKRET_PRICE_3_NAME,
        StringResources.ZAKLINA_WYKRET_PRICE_3_TIME,
        StringResources.ZAKLINA_WYKRET_PRICE_3_AMOUNT
    ),
    PriceItem(
        StringResources.ZAKLINA_WYKRET_PRICE_4_NAME,
        StringResources.ZAKLINA_WYKRET_PRICE_4_TIME,
        StringResources.ZAKLINA_WYKRET_PRICE_4_AMOUNT
    ),
    PriceItem(
        StringResources.ZAKLINA_WYKRET_PRICE_5_NAME,
        StringResources.ZAKLINA_WYKRET_PRICE_5_TIME,
        StringResources.ZAKLINA_WYKRET_PRICE_5_AMOUNT
    ),
    PriceItem(
        StringResources.ZAKLINA_WYKRET_PRICE_6_NAME,
        StringResources.ZAKLINA_WYKRET_PRICE_6_TIME,
        StringResources.ZAKLINA_WYKRET_PRICE_6_AMOUNT
    ),
    PriceItem(
        StringResources.ZAKLINA_WYKRET_PRICE_7_NAME,
        StringResources.ZAKLINA_WYKRET_PRICE_7_TIME,
        StringResources.ZAKLINA_WYKRET_PRICE_7_AMOUNT
    )
)

@Page
@Composable
fun ZaklinaWykretPage() {
    SpecialistPageLayout(
        title = StringResources.ZAKLINA_WYKRET_PAGE_TITLE,
        headerSection = { HeaderSection() },
        profileSection = { ProfileSection() },
        specializationsSection = { SpecializationsSection() },
        servicesSection = { ServicesSection() },
        priceSection = { PriceSection() },
        ctaSection = { context ->
            CTASection(
                ctx = context,
                customAction = { js("window.scrollTo({ top: 0, behavior: 'smooth' })") },
            )
        },
    )
}

@Composable
private fun HeaderSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().margin { auto },
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            AppH1 { Text(StringResources.ZAKLINA_WYKRET_HEADER_NAME.fixPolishOrphans()) }
            AppP(color = SiteTheme.green._800, modifier = Modifier.fontSize(1.25.cssRem)) {
                Text(StringResources.ZAKLINA_WYKRET_HEADER_SUBTITLE.fixPolishOrphans())
            }
            AppP(
                color = SiteTheme.green._800,
                modifier = Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.Bold)
            ) {
                Text(StringResources.ZAKLINA_WYKRET_PHONE)
            }
        }
    }
}

@Composable
private fun ProfileSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        // Large screens: side-by-side image and text
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .flexWrap(FlexWrap.Wrap)
                .displayIfAtLeast(Breakpoint.LG)
                .gap(3.cssRem),
        ) {
            Image(
                src = ImageResources.avatarZaklinaWykret.src,
                alt = ImageResources.avatarZaklinaWykret.description,
                modifier = Modifier
                    .width(400.px)
                    .borderRadius(1.cssRem)
                    .boxShadow(blurRadius = 8.px, color = SiteTheme.black._100)
            )
            Column(
                modifier = Modifier
                    .flex(1)
                    .gap(1.5.cssRem)
                    .padding(1.cssRem),
            ) {
                AppH2 { Text(StringResources.ZAKLINA_WYKRET_O_MNIE_TITLE) }
                AppP(modifier = Modifier.textAlign(TextAlign.Justify)) { Text(StringResources.ZAKLINA_WYKRET_O_MNIE_TEXT_1) }
                AppP(color = SiteTheme.green._800, modifier = Modifier.fontWeight(FontWeight.Bold).textAlign(TextAlign.Justify)) {
                    Text(StringResources.ZAKLINA_WYKRET_O_MNIE_INVITE)
                }
            }
        }
        // Medium and smaller: image above text
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Center)
                .displayUntil(Breakpoint.LG)
                .gap(1.5.cssRem)
                .padding(1.cssRem),
        ) {
            AppH2 { Text(StringResources.ZAKLINA_WYKRET_O_MNIE_TITLE) }
            AppP(modifier = Modifier.textAlign(TextAlign.Justify)) { Text(StringResources.ZAKLINA_WYKRET_O_MNIE_TEXT_1) }
            AppP(color = SiteTheme.green._800, modifier = Modifier.fontWeight(FontWeight.Bold).textAlign(TextAlign.Justify)) {
                Text(StringResources.ZAKLINA_WYKRET_O_MNIE_INVITE)
            }

            Image(
                src = ImageResources.avatarZaklinaWykret.src,
                alt = ImageResources.avatarZaklinaWykret.description,
                modifier = Modifier
                    .widthIn(min = 300.px, max = 500.px)
                    .fillMaxWidth()
                    .borderRadius(1.cssRem)
                    .boxShadow(blurRadius = 8.px, color = SiteTheme.black._100)
            )
        }
    }
}

@Composable
private fun SpecializationsSection() {
    val specializations = listOf(
        SpecialistData(
            icon = { FaComment() },
            title = "Logopedia",
            description = "Diagnoza i terapia zaburzeń mowy i komunikacji u dzieci i dorosłych."
        ),
        SpecialistData(
            icon = { FaHandHoldingMedical() },
            title = "Terapeutyczny taping w logopedii",
            description = "Techniki tapingu wspomagające procesy rehabilitacyjne w terapii mowy."
        ),
        SpecialistData(
            icon = { FaHands() },
            title = "Terapia ręki i stopy I i II stopnia",
            description = "Praca nad poprawą motoryki i koordynacji w kontekście rozwoju mowy."
        ),
        SpecialistData(
            icon = { FaHandsHolding() },
            title = "Terapia NeuroTaktylna MNRI®",
            description = "Wspieranie integracji odruchów pierwotnych i rozwoju układu nerwowego."
        ),
        SpecialistData(
            icon = { FaComments() },
            title = "Komunikacja alternatywna AAC - Mówik, Werbogesty",
            description = "Nowoczesne technologie i metody wspomagające rozwój komunikacji."
        ),
        SpecialistData(
            icon = { FaWind() },
            title = "Trener oddechu",
            description = "Nauka prawidłowych wzorców oddychania wspierających funkcje mowy."
        )
    )

    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50.toRgb().copy(alpha = 128))
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.ZAKLINA_WYKRET_SPECIALIZATIONS_TITLE) }

            SimpleGrid(
                modifier = Modifier.fillMaxWidth().gap(1.5.cssRem),
                numColumns = numColumns(base = 1, sm = 1, md = 2, lg = 2, xl = 2)
            ) {
                specializations.forEach { info ->
                    SpecialistInfoCard(
                        icon = info.icon,
                        title = info.title,
                        description = info.description
                    )
                }
            }
        }
    }
}

@Composable
private fun ServicesSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.ZAKLINA_WYKRET_SERVICES_TITLE.fixPolishOrphans()) }

            SimpleGrid(
                modifier = Modifier.fillMaxWidth().gap(1.cssRem),
                numColumns = numColumns(base = 1, sm = 1, md = 2, lg = 2, xl = 2)
            ) {
                Column(modifier = Modifier.gap(0.5.cssRem)) {
                    Ul {
                        listOf(
                            StringResources.ZAKLINA_WYKRET_SERVICE_1,
                            StringResources.ZAKLINA_WYKRET_SERVICE_2,
                            StringResources.ZAKLINA_WYKRET_SERVICE_3,
                            StringResources.ZAKLINA_WYKRET_SERVICE_4,
                            StringResources.ZAKLINA_WYKRET_SERVICE_5,
                            StringResources.ZAKLINA_WYKRET_SERVICE_6,
                            StringResources.ZAKLINA_WYKRET_SERVICE_7
                        ).forEach { Li { Text(it.fixPolishOrphans()) } }
                    }
                }
                Column(modifier = Modifier.gap(0.5.cssRem)) {
                    Ul {
                        listOf(
                            StringResources.ZAKLINA_WYKRET_SERVICE_8,
                            StringResources.ZAKLINA_WYKRET_SERVICE_9,
                            StringResources.ZAKLINA_WYKRET_SERVICE_10,
                            StringResources.ZAKLINA_WYKRET_SERVICE_11,
                            StringResources.ZAKLINA_WYKRET_SERVICE_12,
                            StringResources.ZAKLINA_WYKRET_SERVICE_13
                        ).forEach { Li { Text(it.fixPolishOrphans()) } }
                    }
                }
            }
        }
    }
}

@Composable
private fun PriceSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50.toRgb().copy(alpha = 128))
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.ZAKLINA_WYKRET_PRICE_TITLE.fixPolishOrphans()) }

            SpecialistPriceTable(priceItems = priceItems)

            AppP(modifier = Modifier.fontSize(0.9.cssRem), color = SiteTheme.black._700) {
                Text(StringResources.ZAKLINA_WYKRET_PRICE_NOTE.fixPolishOrphans())
            }
        }
    }
}
